// src/components/CookieConsent.tsx
'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { CookieConsentConfig } from '../types'
import { useCookieConsent } from '../hooks/useCookieConsent'
import { Banner } from './Banner'
import { SettingsModal } from './SettingsModal'
import { Portal } from './Portal'
import { DebugPanel } from './DebugPanel'
import { FALLBACK_TEXTS, DEFAULT_CONFIG, EVENTS } from '../constants/defaults'
import { mergeStyle } from '../utils/style-helpers'
import { debugLog } from '../utils/helpers'
import '../styles/index.css'

export type CookieConsentProps = Partial<CookieConsentConfig>

export function CookieConsent(props: CookieConsentProps) {
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)

  const {
    consent,
    hasConsent,
    isReady,
    categories,
    acceptAll,
    declineAll,
    setConsent: setConsentHook,
  } = useCookieConsent(props)

  // Merge config
  const config: CookieConsentConfig = {
    ...DEFAULT_CONFIG,
    ...props,
  }

  // Merge style from preset + custom overrides
  const { accentColor, style } = mergeStyle(
    props.preset || config.preset,
    props.style,
    props.accentColor,
    props.borderRadius
  )

  // Get texts based on language
  const texts =
    config.language && FALLBACK_TEXTS[config.language]
      ? FALLBACK_TEXTS[config.language]
      : FALLBACK_TEXTS.en

  // IMPROVED: Show banner if ready AND no consent
  // Removed strict mode === 'strict' check that was blocking display
  useEffect(() => {
    if (isReady && !hasConsent && !hasInteracted) {
      debugLog('Showing banner', { isReady, hasConsent, mode: config.mode })
      setShowBanner(true)
    } else if (hasConsent) {
      debugLog('Banner hidden - user has consent', { hasConsent })
      setShowBanner(false)
    }
  }, [isReady, hasConsent, hasInteracted, config.mode])

  // Listen for manual trigger event
  useEffect(() => {
    const handleSettingsOpen = () => {
      debugLog('Settings opened via event')
      setShowSettings(true)
    }

    window.addEventListener(EVENTS.SETTINGS_OPENED, handleSettingsOpen)

    return () => {
      window.removeEventListener(EVENTS.SETTINGS_OPENED, handleSettingsOpen)
    }
  }, [])

  // Handlers
  const handleAcceptAll = useCallback(() => {
    debugLog('Accept all clicked')
    acceptAll()
    setShowBanner(false)
    setHasInteracted(true)
  }, [acceptAll])

  const handleDecline = useCallback(() => {
    debugLog('Decline clicked')
    declineAll()
    setShowBanner(false)
    setHasInteracted(true)
  }, [declineAll])

  const handleOpenSettings = useCallback(() => {
    debugLog('Settings opened')
    setShowSettings(true)
  }, [])

  const handleCloseSettings = useCallback(() => {
    debugLog('Settings closed')
    setShowSettings(false)
  }, [])

  const handleSaveSettings = useCallback(
    (choices: Record<string, boolean>) => {
      debugLog('Settings saved', choices)
      setConsentHook(choices)
      setShowSettings(false)
      setShowBanner(false)
      setHasInteracted(true)
    },
    [setConsentHook]
  )

  const handleClose = useCallback(() => {
    // Close button only works in soft mode OR if user can dismiss without choosing
    if (config.closeButton) {
      debugLog('Banner closed via X button', { mode: config.mode })
      setShowBanner(false)
      setHasInteracted(true)

      // In soft mode, treat close as implicit acceptance of required categories
      if (config.mode === 'soft') {
        declineAll() // This accepts only required categories
      }
    }
  }, [config.closeButton, config.mode, declineAll])

  const handleConsentChange = useCallback(
    (categoryId: string, enabled: boolean) => {
      debugLog('Consent changed', { categoryId, enabled })
      setConsentHook({
        ...consent,
        [categoryId]: enabled,
      })
    },
    [consent, setConsentHook]
  )

  if (!isReady) {
    debugLog('Not ready yet')
    return null
  }

  return (
    <>
      <Portal zIndex={999997}>
        <AnimatePresence mode="wait">
          {showBanner && (
            <Banner
              key="banner"
              config={config}
              texts={texts}
              categories={categories}
              consent={consent}
              onAccept={handleAcceptAll}
              onDecline={handleDecline}
              onOpenSettings={handleOpenSettings}
              onClose={handleClose}
              onConsentChange={handleConsentChange}
              accentColor={accentColor}
              style={style}
            />
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {showSettings && (
            <SettingsModal
              key="settings"
              config={config}
              texts={texts}
              categories={categories}
              currentConsent={consent}
              onSave={handleSaveSettings}
              onCancel={handleCloseSettings}
              accentColor={accentColor}
              style={style}
            />
          )}
        </AnimatePresence>
      </Portal>

      {/* Debug panel in development */}
      {config.debug && (
        <DebugPanel
          debugInfo={{
            isReady,
            hasConsent,
            showBanner,
            showSettings,
            mode: config.mode || 'strict',
            consent,
            categories,
            position: style.position,
            preset: config.preset || 'default',
            zIndex: 999997,
          }}
        />
      )}
    </>
  )
}

// Export standalone functions for manual control
export { showCookieSettings, revokeConsent, exportConsent } from './api'
